import {useCallback} from 'react';

import {MainBreakoutRoomId} from '../constants';
import type {
    BreakoutRoomId,
    BreakoutRoomNames,
    BreakoutsEditPanelSave,
    ParticipantID,
} from '../types';

/**
 *
 * @param localParticipants - The local end result of the edit
 * @param liveParticipants - The back-end view of the participants. What everyone currently sees
 * @param localBreakoutNames - The local end result of editing the names of existing breakout rooms
 * @param liveBreakoutNames - The back-end view of existing breakout rooms. What everyone currently sees
 * @returns - Object with actions to perform in order to align the back-end state with the local state.
 */
export const useBreakoutEditDiff = ({
    localParticipants,
    liveParticipants,
    localBreakoutNames,
    liveBreakoutNames,
}: {
    localParticipants: Map<BreakoutRoomId, ParticipantID[]>;
    liveParticipants: Map<BreakoutRoomId, ParticipantID[]>;
    localBreakoutNames: BreakoutRoomNames;
    liveBreakoutNames: BreakoutRoomNames;
}) =>
    useCallback(() => {
        const actions: BreakoutsEditPanelSave = {
            move: new Map(),
            addRoom: new Map(),
            renameRoom: new Map(),
            removeRoom: [],
        };
        for (const existingRoomId of liveBreakoutNames.keys()) {
            if (!localParticipants.has(existingRoomId)) {
                // add to removeRoom actions
                actions.removeRoom.push(existingRoomId);
                continue;
            }
            const localName = localBreakoutNames.get(existingRoomId);
            if (
                localName &&
                localName !== liveBreakoutNames.get(existingRoomId)
            ) {
                // add to rename actions
                actions.renameRoom.set(existingRoomId, localName);
            }
        }
        for (const roomId of localParticipants.keys()) {
            if (roomId === MainBreakoutRoomId) {
                // main is not a breakout room
                continue;
            }
            /**
             * Note: The fact that we don't allow duplicate room names is what
             * saves us from a false positive where someone renamed a room whilst we were editing
             */
            if (!liveBreakoutNames.has(roomId)) {
                // add to addRoom actions
                actions.addRoom.set(roomId, new Map());
            }
        }
        for (const [roomId, participants] of localParticipants.entries()) {
            for (const localParticipant of participants) {
                for (const [
                    liveRoomId,
                    liveRoomParticipants,
                ] of liveParticipants.entries()) {
                    for (const liveParticipant of liveRoomParticipants) {
                        if (localParticipant === liveParticipant) {
                            if (roomId !== liveRoomId) {
                                // move that participant to roomId
                                if (actions.addRoom.has(roomId)) {
                                    // moving participants to a new room
                                    const newRoomParticipants =
                                        actions.addRoom.get(roomId);
                                    if (newRoomParticipants) {
                                        const participants =
                                            newRoomParticipants.get(liveRoomId);
                                        if (participants) {
                                            participants.push(localParticipant);
                                        } else {
                                            newRoomParticipants.set(
                                                liveRoomId,
                                                [localParticipant],
                                            );
                                        }
                                    }
                                    continue;
                                }
                                // moving participants to an existing room
                                const oldRoom =
                                    actions.move.get(liveRoomId) ?? new Map();
                                const newRoom = oldRoom?.get(roomId);
                                if (newRoom) {
                                    newRoom.push(localParticipant);
                                } else {
                                    oldRoom.set(roomId, [localParticipant]);
                                }
                                actions.move.set(liveRoomId, oldRoom);
                            }
                        }
                    }
                }
            }
        }
        return actions;
    }, [
        liveBreakoutNames,
        localBreakoutNames,
        liveParticipants,
        localParticipants,
    ]);
